import { ContactInfo, MarketoForm, useContact, useMarketo } from '@/context';
import { MarketoFormProps } from '@/features/MarketoForm/MarketoForm.interface';
import { useCRMSync } from '@/features/MarketoForm/hooks/useCRMSync';
import React, { useCallback } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { ToggleGroupProps } from './components/ToggleGroup/ToggleGroup.interface';

type CustomForm = {
  groups: ToggleGroupProps[];
  marketoProps?: MarketoFormProps;
  onSuccess?: () => void;
};

const useCustomForm = ({ groups, marketoProps, onSuccess }: CustomForm) => {
  const { contactInfo } = useContact();
  const [marketoForm, setMarketoForm] = React.useState<MarketoForm>();
  const [submitting, setSubmitting] = React.useState(false);

  const { loadForm } = useMarketo();

  const CRMSyncFieldnames = React.useMemo(
    () =>
      groups?.reduce((fieldnames, group) => {
        const multipleFieldNames: string[] = [];

        return [
          ...fieldnames,
          ...group.options
            .filter(({ crmName, isMultiple }) => {
              if (!isMultiple) return true;

              if (multipleFieldNames.includes(crmName)) return false;

              multipleFieldNames.push(crmName);

              return true;
            })
            .map(({ crmName, marketoName, isMultiple }) => ({
              crmName,
              marketoName,
              isMultiple,
            })),
        ];
      }, [] as ICRMSyncFieldnames[]) || [],
    [groups],
  );

  // Default values
  const getDefaultFormValues = React.useCallback(
    () =>
      CRMSyncFieldnames.reduce((prev, curr) => {
        const defaultValue = curr.isMultiple ? [] : '';
        const value = contactInfo ? contactInfo[curr.crmName as keyof ContactInfo] : '';
        const nextValue = curr.isMultiple ? value?.toString().split(',') : value;

        return {
          ...prev,
          [`${curr.marketoName}`]: nextValue || defaultValue,
        };
      }, {}),
    [CRMSyncFieldnames, contactInfo],
  );

  const form = useForm({
    mode: 'onChange',
    defaultValues: getDefaultFormValues(),
  });

  const handleSuccess = useCallback(() => {
    form.reset(getDefaultFormValues(), { keepValues: true });
    setSubmitting(false);
    onSuccess?.();
  }, [getDefaultFormValues, form, onSuccess]);

  const { syncCRMValues, submitCRMSyncValues } = useCRMSync({
    CRMSyncFieldnames,
    emailFieldname: marketoProps?.emailFieldname,
    emailCRMFieldname: marketoProps?.emailCRMFieldname,
  });

  // Load form
  React.useEffect(() => {
    if (marketoForm) return;
    if (!marketoProps?.formId) return;

    loadForm({ formId: marketoProps?.formId, onSuccess: handleSuccess }).then((mktoForm) => {
      if (!mktoForm) return;

      syncCRMValues(mktoForm);
      setMarketoForm(mktoForm);
    });
  }, [marketoForm, marketoProps?.formId, loadForm, handleSuccess, syncCRMValues]);

  const onSubmit = React.useCallback(
    async (formData: Record<string, string | string[]> = {}) => {
      if (!marketoForm) return;

      setSubmitting(true);

      const values = Object.keys(formData).reduce(
        (d, key) => ({
          ...d,
          [key]: formData[key].toString(),
        }),
        {},
      );

      marketoForm.addHiddenFields(values);

      await submitCRMSyncValues(marketoForm, values).catch((err) => {
        console.log('err', { err });
        setSubmitting(false);
      });

      marketoForm.submit();
    },
    [marketoForm, submitCRMSyncValues],
  );

  const state = useFormState({ control: form.control });

  return {
    ...form,
    state,
    handleSubmit: form.handleSubmit(onSubmit),
    submitting,
  };
};

const useCustomToast = ({ confirmationMessage }: Partial<IToggleMenu> & { reset?: () => void }) => {
  const [messageBarOpen, setMessageBarOpen] = React.useState<boolean>(false);
  const handleCloseMessageBar = React.useCallback(() => {
    setMessageBarOpen(false);
  }, []);
  const messageBarProps = React.useMemo(
    () => ({
      isOpen: messageBarOpen,
      onClose: handleCloseMessageBar,
      message: confirmationMessage,
      autoHideDuration: 6000,
    }),
    [confirmationMessage, handleCloseMessageBar, messageBarOpen],
  );
  const handleSuccess = React.useCallback(() => setMessageBarOpen(true), []);

  return { messageBarProps, handleSuccess };
};

type IToggleMenu = CustomForm & {
  confirmationMessage?: string;
};

export const useToggleMenu = ({ groups, confirmationMessage, marketoProps }: IToggleMenu) => {
  const { handleSuccess: handleToastSuccess, ...toast } = useCustomToast({ confirmationMessage });
  const { state, ...form } = useCustomForm({
    groups,
    marketoProps,
    onSuccess: handleToastSuccess,
  });

  return {
    form,
    state,
    ...toast,
  };
};
